import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Heading,
  Loader,
  Flex,
  Text,
  TextField,
  Dropdown,
} from '@vibe/core';
import {
  getLeaveTypes,
  createLeaveRequest,
  updateLeaveRequest,
  deleteLeaveRequest,
  type LeaveType,
  type LeaveRequest,
} from '../../api/leave.api';
import { getUserSettings, getMyApprovees, getLeaveApprovers, type LeaveApproverMapping } from '../../api/settings.api';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';
import { type DaySchedule, DEFAULT_DAY_SCHEDULE } from '../../api/capacity.api';
import { fetchUserSchedules } from '../../services/workload-data.service';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Map JS getDay() index (0=Sun) to DaySchedule key. */
const DAY_INDEX_TO_KEY: (keyof DaySchedule)[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** Calculate working days between two dates (inclusive), respecting the capacity schedule. */
function calcBusinessDays(from: string, to: string, schedule: DaySchedule): number {
  const start = new Date(from);
  const end = new Date(to);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return 0;
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const dayKey = DAY_INDEX_TO_KEY[cur.getDay()];
    if (schedule[dayKey] > 0) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

// ─── Status Badge ────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
  pending: { bg: '#fdab3d', color: '#fff', label: 'Pending' },
  approved: { bg: '#00c875', color: '#fff', label: 'Approved' },
  rejected: { bg: '#e2445c', color: '#fff', label: 'Rejected' },
  recalled: { bg: '#676879', color: '#fff', label: 'Recalled' },
  recalling: { bg: '#fdab3d', color: '#fff', label: 'Recalling' },
};

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LeaveRequestDialogProps {
  currentUserId: number;
  /** Pre-filled start date (e.g. from scheduler cell click) */
  initialDate?: string;
  /** Pre-filled end date (optional; defaults to initialDate). Used when opening
   *  the dialog from a multi-day drag selection. */
  initialEndDate?: string;
  /** When provided, the dialog operates in edit mode for an existing pending leave */
  editingRequest?: LeaveRequest;
  /** Pre-selected user to create leave on behalf of (from scheduler) */
  forUserId?: number;
  forUserName?: string;
  /** Whether the current user is an app admin */
  isAdmin?: boolean;
  onSubmitted: (request: LeaveRequest) => void;
  onDeleted?: (id: string) => void;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const LeaveRequestDialog: React.FC<LeaveRequestDialogProps> = ({
  currentUserId,
  initialDate,
  initialEndDate,
  editingRequest,
  forUserId,
  forUserName,
  isAdmin,
  onSubmitted,
  onDeleted,
  onClose,
}) => {
  const isEditMode = !!editingRequest;

  const toDateStr = (d?: string) => (d ? d.split('T')[0] : '');

  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState<string>(
    editingRequest?.leave_type_id ?? '',
  );
  const [dateFrom, setDateFrom] = useState(
    editingRequest ? toDateStr(editingRequest.date_from) : (initialDate ?? ''),
  );
  const [dateTo, setDateTo] = useState(
    editingRequest
      ? toDateStr(editingRequest.date_to)
      : (initialEndDate ?? initialDate ?? ''),
  );
  const [note, setNote] = useState(editingRequest?.note ?? '');
  const [capacityType, setCapacityType] = useState<'full_day' | 'half_day'>(
    editingRequest?.capacity_type ?? 'full_day',
  );

  const [approverId, setApproverId] = useState<number | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<MondayUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAdminApprover, setHasAdminApprover] = useState(false);
  const [noAssignedApprover, setNoAssignedApprover] = useState(false);
  const [userSchedule, setUserSchedule] = useState<DaySchedule>(DEFAULT_DAY_SCHEDULE);

  // Approver-on-behalf state: users the current user can create leave for
  const [approveeUsers, setApproveeUsers] = useState<MondayUser[]>([]);
  const [approverMappings, setApproverMappings] = useState<LeaveApproverMapping[]>([]);
  const [selectedOnBehalfUserId, setSelectedOnBehalfUserId] = useState<number | undefined>(forUserId);
  const [selectedOnBehalfUserName, setSelectedOnBehalfUserName] = useState<string | undefined>(forUserName);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const effectiveForUserId = selectedOnBehalfUserId ?? forUserId;
        const targetUserId = String(effectiveForUserId ?? currentUserId);
        const [types, userSettings, users, scheduleResult, approvees, mappings] = await Promise.all([
          getLeaveTypes(),
          getUserSettings(),
          fetchMondayUsers(),
          fetchUserSchedules([targetUserId]),
          isEditMode ? Promise.resolve([]) : getMyApprovees(),
          isEditMode || !isAdmin ? Promise.resolve([]) : getLeaveApprovers(),
        ]);
        const targetSchedule = scheduleResult.userSchedules.get(targetUserId);
        setUserSchedule(
          targetSchedule
            ? (targetSchedule as unknown as DaySchedule)
            : scheduleResult.defaultSchedule,
        );
        setLeaveTypes(types);
        if (editingRequest) {
          setSelectedLeaveTypeId(editingRequest.leave_type_id);
        } else if (types.length > 0 && !selectedLeaveTypeId) {
          setSelectedLeaveTypeId(types[0].id);
        }
        setAllUsers(users);

        // Build approvee user list (users the current user can create leave for)
        if (approvees.length > 0) {
          const approveeList = approvees
            .map((a) => users.find((u) => String(u.id) === a.mondayUserId))
            .filter((u): u is MondayUser => !!u);
          setApproveeUsers(approveeList);
        }
        if (mappings.length > 0) {
          setApproverMappings(mappings);
        }

        setNoAssignedApprover(false);

        if (editingRequest?.approver_user_id) {
          const editApproverId = Number(editingRequest.approver_user_id);
          setApproverId(editApproverId);
          const user = users.find((u) => u.id === editApproverId);
          if (user) setApproverName(user.name);
          if (userSettings.defaultLeaveApprover) {
            setHasAdminApprover(true);
          }
        } else if (effectiveForUserId) {
          // When creating on behalf, look up the target user's designated approver
          const targetMapping = mappings.find(
            (m) => m.mondayUserId === String(effectiveForUserId),
          );
          if (targetMapping?.defaultLeaveApprover) {
            // Target user has a designated approver — lock to it
            const designatedApproverId = Number(targetMapping.defaultLeaveApprover);
            setApproverId(designatedApproverId);
            setHasAdminApprover(true);
            const user = users.find((u) => u.id === designatedApproverId);
            if (user) setApproverName(user.name);
          } else {
            // No designated approver for this user — check if current user is their approver via approvees
            const isApproveeOfCurrent = approvees.some(
              (a) => a.mondayUserId === String(effectiveForUserId),
            );
            if (isApproveeOfCurrent) {
              setApproverId(Number(currentUserId));
              setHasAdminApprover(true);
              const user = users.find((u) => u.id === Number(currentUserId));
              if (user) setApproverName(user.name);
            } else {
              // Admin creating for a user with no designated approver — leave editable
              setHasAdminApprover(false);
              setNoAssignedApprover(true);
              setApproverId(null);
              setApproverName(null);
            }
          }
        } else if (userSettings.defaultLeaveApprover) {
          const adminApproverId = Number(userSettings.defaultLeaveApprover);
          setApproverId(adminApproverId);
          setHasAdminApprover(true);
          const user = users.find((u) => u.id === adminApproverId);
          if (user) {
            setApproverName(user.name);
          }
        } else {
          // No approver configured — will auto-approve
          setNoAssignedApprover(true);
          setApproverId(null);
          setApproverName(null);
          setHasAdminApprover(false);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to load leave data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [currentUserId, forUserId, selectedOnBehalfUserId]);

  // Compute business days and total
  const businessDays = useMemo(() => {
    if (!dateFrom || !dateTo) return 0;
    return calcBusinessDays(dateFrom, dateTo, userSchedule);
  }, [dateFrom, dateTo, userSchedule]);

  const totalDays = useMemo(() => {
    return capacityType === 'half_day' ? businessDays * 0.5 : businessDays;
  }, [businessDays, capacityType]);

  const leaveTypeOptions = leaveTypes.map((lt) => ({
    value: lt.id,
    label: lt.name,
  }));

  const approverDropdownOptions = useMemo(() => {
    const userOpts = allUsers.map((u) => ({
      value: u.id,
      label: u.name,
    }));
    return [{ value: -1, label: '-- None --' }, ...userOpts];
  }, [allUsers]);

  const approverValue = approverId
    ? {
        value: approverId,
        label: approverName ?? `User ${approverId}`,
      }
    : { value: -1, label: '-- None --' };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    if (!selectedLeaveTypeId || !dateFrom || !dateTo) {
      setError('Please fill in all required fields.');
      return;
    }

    if (totalDays < 0.5) {
      setError('The selected date range must include at least half a working day.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let request: LeaveRequest;
      if (isEditMode && editingRequest) {
        request = await updateLeaveRequest(editingRequest.id, {
          leaveTypeId: selectedLeaveTypeId,
          dateFrom,
          dateTo,
          capacityType,
          totalDays,
          note: note || undefined,
        });
      } else {
        const effectiveForUserId = selectedOnBehalfUserId ?? forUserId;
        request = await createLeaveRequest({
          leaveTypeId: selectedLeaveTypeId,
          approverUserId: approverId ? String(approverId) : undefined,
          dateFrom,
          dateTo,
          capacityType,
          totalDays,
          note: note || undefined,
          forUserId: effectiveForUserId ? String(effectiveForUserId) : undefined,
        });
      }
      onSubmitted(request);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to submit leave request';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isEditMode,
    editingRequest,
    selectedLeaveTypeId,
    dateFrom,
    dateTo,
    approverId,
    capacityType,
    totalDays,
    note,
    forUserId,
    selectedOnBehalfUserId,
    onSubmitted,
  ]);

  // ─── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    if (!editingRequest) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteLeaveRequest(editingRequest.id);
      onDeleted?.(editingRequest.id);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete leave request';
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [editingRequest, onDeleted, onClose]);

  // ─── Close on overlay click ─────────────────────────────────────────────────

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ─── Status info ──────────────────────────────────────────────────────────

  const statusInfo = editingRequest
    ? STATUS_STYLES[editingRequest.status] ?? STATUS_STYLES.pending
    : null;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="rp-leave-dialog-overlay" onClick={handleOverlayClick}>
      <div className="rp-leave-dialog">
        {/* Header */}
        <div className="rp-leave-dialog__header">
          <Heading type={Heading.types.H2}>Request Leave</Heading>
          <button className="rp-leave-dialog__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="rp-leave-dialog__loading">
            <Loader size={Loader.sizes.MEDIUM} />
          </div>
        ) : (
          <div className="rp-leave-dialog__body">
            {/* Note when creating leave for another user (from scheduler) */}
            {forUserId && forUserName && (
              <div
                style={{
                  background: '#e5f4ff',
                  border: '1px solid #0073ea',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: 12,
                  fontSize: 13,
                  color: '#323338',
                }}
              >
                You are creating a leave request on behalf of <strong>{forUserName}</strong>.
                {approveeUsers.some((u) => u.id === forUserId)
                  ? ' As their designated approver, the request will be auto-approved.'
                  : isAdmin
                    ? ' As an app admin, you can request leaves for any user from the scheduler.'
                    : ''}
              </div>
            )}

            {/* Self-approver note */}
            {!isEditMode && !selectedOnBehalfUserId && !forUserId && approverId === Number(currentUserId) && !noAssignedApprover && (
              <div
                style={{
                  background: '#e5fff0',
                  border: '1px solid #00c875',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontSize: 13,
                  color: '#323338',
                }}
              >
                You are your own designated approver. This request will be auto-approved.
              </div>
            )}

            {/* No approver assigned note */}
            {!isEditMode && noAssignedApprover && !approverId && (
              <div
                style={{
                  background: '#fff3e0',
                  border: '1px solid #fdab3d',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontSize: 13,
                  color: '#323338',
                }}
              >
                No approver has been assigned{selectedOnBehalfUserId || forUserId ? ' to this user' : ''}. This request will be <strong>auto-approved</strong>.
                You can also select an approver below if you'd like someone to review it first.
              </div>
            )}

            {/* User picker for creating leave on behalf (admins see all users, approvers see their approvees) */}
            {!isEditMode && !forUserId && (isAdmin || approveeUsers.length > 0) && (
              <div className="rp-leave-dialog__field">
                <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                  Request for
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  options={[
                    { value: -1, label: 'Myself' },
                    ...(isAdmin
                      ? allUsers
                          .filter((u) => u.id !== Number(currentUserId))
                          .map((u) => ({ value: u.id, label: u.name }))
                      : approveeUsers.map((u) => ({ value: u.id, label: u.name }))
                    ),
                  ]}
                  value={
                    selectedOnBehalfUserId
                      ? {
                          value: selectedOnBehalfUserId,
                          label:
                            selectedOnBehalfUserName ??
                            `User ${selectedOnBehalfUserId}`,
                        }
                      : { value: -1, label: 'Myself' }
                  }
                  onChange={(option) => {
                    if (!option) {
                      setSelectedOnBehalfUserId(undefined);
                      setSelectedOnBehalfUserName(undefined);
                      return;
                    }
                    const selected = option as { value: number; label: string };
                    if (selected.value === -1) {
                      setSelectedOnBehalfUserId(undefined);
                      setSelectedOnBehalfUserName(undefined);
                    } else {
                      setSelectedOnBehalfUserId(selected.value);
                      setSelectedOnBehalfUserName(selected.label);
                    }
                  }}
                  placeholder="Search user..."
                />
                {selectedOnBehalfUserId && selectedOnBehalfUserName && (
                  <div
                    style={{
                      background: '#e5f4ff',
                      border: '1px solid #0073ea',
                      borderRadius: 6,
                      padding: '8px 12px',
                      marginTop: 8,
                      fontSize: 13,
                      color: '#323338',
                    }}
                  >
                    Creating a leave request on behalf of <strong>{selectedOnBehalfUserName}</strong>.
                    {approveeUsers.some((u) => u.id === selectedOnBehalfUserId)
                      ? ' As their designated approver, the request will be auto-approved.'
                      : ' The request will be sent to the designated approver for approval.'}
                  </div>
                )}
              </div>
            )}

            {/* Row 1: Leave Type + Approver side by side */}
            <div className="rp-leave-dialog__row">
              <div className="rp-leave-dialog__field rp-leave-dialog__field--half">
                <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                  Leave Type
                </Text>
                <Dropdown
                  placeholder="Select leave type"
                  options={leaveTypeOptions}
                  value={leaveTypeOptions.find((o) => o.value === selectedLeaveTypeId) ?? null}
                  onChange={(option: { value: string; label: string } | null) => {
                    setSelectedLeaveTypeId(option?.value ?? '');
                  }}
                  size={Dropdown.sizes.SMALL}
                />
              </div>
              <div className="rp-leave-dialog__field rp-leave-dialog__field--half">
                <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                  Approver
                </Text>
                <Dropdown
                  size={Dropdown.sizes.SMALL}
                  options={approverDropdownOptions}
                  value={approverValue}
                  onChange={(option) => {
                    if (!option) {
                      setApproverId(null);
                      setApproverName(null);
                      if (noAssignedApprover) setNoAssignedApprover(true);
                      return;
                    }
                    const selected = option as { value: number; label: string };
                    if (selected.value === -1) {
                      setApproverId(null);
                      setApproverName(null);
                    } else {
                      setApproverId(selected.value);
                      setNoAssignedApprover(false);
                      const matchedUser = allUsers.find(
                        (u) => u.id === selected.value,
                      );
                      setApproverName(matchedUser?.name ?? null);
                    }
                  }}
                  placeholder="Search approver..."
                  clearable
                  disabled={hasAdminApprover}
                />
                {hasAdminApprover && (
                  <Text
                    type={Text.types.TEXT2}
                    style={{ color: '#676879', marginTop: 2, fontSize: 11 }}
                  >
                    Assigned by admin
                  </Text>
                )}
              </div>
            </div>

            {/* Note field */}
            <div className="rp-leave-dialog__field">
              <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                Note
              </Text>
              <TextField
                placeholder="Enter your note here"
                size={TextField.sizes.SMALL}
                value={note}
                onChange={(value: string) => setNote(value)}
              />
            </div>

            {/* Info panel: Leave Days + Capacity + Summary */}
            <div className="rp-leave-dialog__row rp-leave-dialog__row--info">
              <div className="rp-leave-dialog__info-left">
                <div className="rp-leave-dialog__field">
                  <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                    Leave Days
                  </Text>
                  <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
                    <div style={{ flex: 1 }}>
                      <TextField
                        type={TextField.types.DATE}
                        size={TextField.sizes.SMALL}
                        value={dateFrom}
                        onChange={(value: string) => setDateFrom(value)}
                      />
                    </div>
                    <Text type={Text.types.TEXT2} style={{ color: '#676879', flexShrink: 0 }}>-</Text>
                    <div style={{ flex: 1 }}>
                      <TextField
                        type={TextField.types.DATE}
                        size={TextField.sizes.SMALL}
                        value={dateTo}
                        onChange={(value: string) => setDateTo(value)}
                      />
                    </div>
                  </Flex>
                </div>

                {/* Capacity toggle */}
                <div className="rp-leave-dialog__field">
                  <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                    Capacity
                  </Text>
                  <div className="rp-leave-dialog__cap-toggle">
                    <button
                      type="button"
                      className={`rp-leave-dialog__cap-btn ${capacityType === 'full_day' ? 'rp-leave-dialog__cap-btn--active' : ''}`}
                      onClick={() => setCapacityType('full_day')}
                    >
                      Full day
                    </button>
                    <button
                      type="button"
                      className={`rp-leave-dialog__cap-btn ${capacityType === 'half_day' ? 'rp-leave-dialog__cap-btn--active' : ''}`}
                      onClick={() => setCapacityType('half_day')}
                    >
                      Half day
                    </button>
                  </div>
                </div>

                {isEditMode && statusInfo && (
                  <div className="rp-leave-dialog__status-row">
                    <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
                      Status
                    </Text>
                    <span
                      className="rp-leave-dialog__status-badge"
                      style={{ background: statusInfo.bg, color: statusInfo.color }}
                    >
                      {statusInfo.label}
                    </span>
                  </div>
                )}
              </div>
              <div className="rp-leave-dialog__info-divider" />
              <div className="rp-leave-dialog__info-right">
                <div className="rp-leave-dialog__summary-item">
                  <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                    Total Taken Days
                  </Text>
                  <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                    {totalDays} {totalDays === 1 ? 'day' : 'days'}
                  </Text>
                </div>
                {dateFrom && dateTo && (
                  <div className="rp-leave-dialog__summary-item">
                    <Text type={Text.types.TEXT2} style={{ color: '#676879' }}>
                      Working Days in Range
                    </Text>
                    <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
                      {businessDays} {businessDays === 1 ? 'day' : 'days'}
                    </Text>
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* Error (outside scrollable body so it's always visible) */}
        {error && (
          <div style={{ padding: '0 28px' }}>
            <Text style={{ color: '#e2445c', fontSize: 13 }}>
              {error}
            </Text>
          </div>
        )}

        {/* Footer */}
        <div className="rp-leave-dialog__footer">
          <Button kind={Button.kinds.TERTIARY} onClick={onClose}>
            Cancel
          </Button>
          {isEditMode && editingRequest?.status === 'pending' && (
            <Button
              kind={Button.kinds.SECONDARY}
              color={Button.colors.NEGATIVE}
              onClick={handleDelete}
              loading={isDeleting}
            >
              Remove
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={
              isLoading ||
              !selectedLeaveTypeId ||
              !dateFrom ||
              !dateTo
            }
          >
            {isEditMode ? 'Update' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LeaveRequestDialog;
